import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Grid, Typography, Box, IconButton, Alert, Chip,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { validateFormula } from '../../utils/formulaParser';

export default function CalculatedColumnDialog({ open, onClose, availableColumns, existingCalc, onSave }) {
  const [key, setKey] = useState('');
  const [headerName, setHeaderName] = useState('');
  const [formula, setFormula] = useState('');
  const [formatType, setFormatType] = useState('number');
  const [error, setError] = useState('');
  const [referencedCols, setReferencedCols] = useState([]);

  useEffect(() => {
    if (existingCalc) {
      setKey(existingCalc.key || '');
      setHeaderName(existingCalc.headerName || '');
      setFormula(existingCalc.formula || '');
      setFormatType(existingCalc.format?.type || 'number');
    } else {
      setKey('');
      setHeaderName('');
      setFormula('');
      setFormatType('number');
    }
    setError('');
    setReferencedCols([]);
  }, [existingCalc, open]);

  const handleFormulaChange = (val) => {
    setFormula(val);
    setError('');
    if (val.trim()) {
      const colKeys = availableColumns.map(c => c.key || c.value);
      const result = validateFormula(val, colKeys);
      if (!result.valid) {
        setError(result.error);
      }
      setReferencedCols(result.referencedColumns || []);
    } else {
      setReferencedCols([]);
    }
  };

  const handleSave = () => {
    if (!headerName.trim()) { setError('Column name is required'); return; }
    if (!formula.trim()) { setError('Formula is required'); return; }

    const colKeys = availableColumns.map(c => c.key || c.value);
    const result = validateFormula(formula, colKeys);
    if (!result.valid) { setError(result.error); return; }

    // Generate key from header name if not editing
    const calcKey = key || `calc_${headerName.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_')}`;

    const formatConfig = formatType === 'currency'
      ? { type: 'currency', symbol: '$', decimals: 2 }
      : formatType === 'percentage'
        ? { type: 'percentage', decimals: 2 }
        : { type: 'number', decimals: 2 };

    onSave({
      key: calcKey,
      headerName: headerName.trim(),
      formula: formula.trim(),
      format: formatConfig,
      referencedColumns: result.referencedColumns,
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        {existingCalc ? 'Edit Calculated Column' : 'Add Calculated Column'}
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Column Name" value={headerName}
              onChange={(e) => setHeaderName(e.target.value)}
              placeholder="e.g., Remaining Balance"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth label="Formula" value={formula}
              onChange={(e) => handleFormulaChange(e.target.value)}
              placeholder="e.g., total_pay - paid_amount"
              multiline rows={2}
              helperText="Use column keys with +, -, *, / operators and parentheses"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select fullWidth label="Format" value={formatType}
              onChange={(e) => setFormatType(e.target.value)}
              size="small"
              SelectProps={{ native: true }}
            >
              <option value="number">Number (0.00)</option>
              <option value="currency">Currency ($0.00)</option>
              <option value="percentage">Percentage (0.00%)</option>
            </TextField>
          </Grid>
        </Grid>

        {/* Available columns reference */}
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            Available Columns:
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            {availableColumns.map(col => (
              <Chip
                key={col.key || col.value}
                label={`${col.label} (${col.key || col.value})`}
                size="small"
                variant={referencedCols.includes(col.key || col.value) ? 'filled' : 'outlined'}
                color={referencedCols.includes(col.key || col.value) ? 'primary' : 'default'}
                onClick={() => {
                  const colKey = col.key || col.value;
                  const newFormula = formula ? `${formula} ${colKey}` : colKey;
                  handleFormulaChange(newFormula);
                }}
                sx={{ cursor: 'pointer', fontSize: '0.7rem' }}
              />
            ))}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave}>
          {existingCalc ? 'Update' : 'Add Column'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
